import { Userpic } from "@humansignal/ui";
import { cn } from "@afriannotate/datamanager/utils/bem";
import "./RecordCells.prefix.css";

const stackCN = cn("record-photos");
const whoCN = cn("record-contributor");

/** A record's photos as a stack. Only the FIRST photo is loaded: the
 *  others are drawn as edges behind it with a count, so a list of records
 *  costs one small image per row, not three. ``size`` "card" is the large
 *  version on grid cards. */
export const PhotoStack = ({ photos, total, fallback, size = "cell", waiting = 0 }) => {
  const list = Array.isArray(photos) ? photos.filter(Boolean) : [];
  if (!list.length) {
    return waiting ? <span className={stackCN.elem("waiting").toClassName()}>waiting to upload</span> : null;
  }
  const count = Math.max(total ?? list.length, 1);
  return (
    <span
      className={stackCN.mod({ size, stacked: count > 1 }).toClassName()}
      title={`${count} photo${count === 1 ? "" : "s"}`}
    >
      <img
        className={stackCN.elem("photo").toClassName()}
        src={list[0]}
        alt=""
        loading="lazy"
        onError={(e) => {
          // No thumbnail (an older server, an unreadable image): the photo.
          if (fallback && e.currentTarget.src !== fallback && !e.currentTarget.dataset.fellBack) {
            e.currentTarget.dataset.fellBack = "1";
            e.currentTarget.src = fallback;
          }
        }}
      />
      {count > 1 ? <span className={stackCN.elem("more").toClassName()}>+{count - 1}</span> : null}
    </span>
  );
};

/** Records table "Photo" column (matched by the column's name). */
export const PhotoStackCell = ({ original, value }) => {
  return (
    <PhotoStack
      photos={value ? [value] : []}
      total={original?.extra?.photos?.length}
      fallback={original?.extra?.photo_full}
    />
  );
};
PhotoStackCell.displayType = false;

/** Who saved a record: avatar (or initials) and name. */
export const Contributor = ({ user, name }) => (
  <span className={whoCN.toClassName()}>
    {user ? <Userpic user={user} /> : <span className={whoCN.elem("blank").toClassName()} aria-hidden="true" />}
    <span className={whoCN.elem("name").toClassName()}>{name}</span>
  </span>
);

/** Records table "Contributor" column (matched by the column's name). */
export const ContributorCell = ({ original, value }) => {
  if (!value && !original?.extra?.contributor_user) return null;
  return <Contributor user={original?.extra?.contributor_user} name={value} />;
};
ContributorCell.displayType = false;
